import { useEffect, useState, useCallback } from "react";
import { api, errorMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Panel } from "@/components/Panel";
import { MultiCombobox } from "@/components/Combobox";
import { ServicioFusionDialog } from "@/components/ServicioFusionDialog";
import { ServicioDuplicadosDialog } from "@/components/ServicioDuplicadosDialog";
import { useBusquedaLive } from "@/lib/busquedaHeader";
import { Pencil, X, Plus, LayoutList, GitMerge, Copy, ChevronDown, ChevronUp, ChevronRight } from "lucide-react";

const VACIO = { nombre: "", activo: true, solo_cronograma: false, solicita_repuestos: false, abre_cronograma: false, garantia_honda: false, plantillas_asociadas: [] };

const Campo = ({ label, obligatorio, error, testId, children }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium text-slate-700">
      {label} {obligatorio && <span className="text-red-500">*</span>}
    </label>
    {children}
    {error && <p data-testid={`${testId}-error`} className="text-xs text-red-500">{error}</p>}
  </div>
);

const EstadoBadge = ({ activo }) => (
  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
    activo ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"}`}>
    {activo ? "Activo" : "Inactivo"}
  </span>
);

export default function Servicios() {
  const { isAdmin } = useAuth();
  const [servicios, setServicios] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [q] = useBusquedaLive("Buscar en servicios…");
  const [form, setForm] = useState(null);
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [fusionAbierto, setFusionAbierto] = useState(false);
  const [fusionIds, setFusionIds] = useState([]);
  const [dupAbierto, setDupAbierto] = useState(false);
  const [expandidos, setExpandidos] = useState(new Set());
  const [soloCronoOpen, setSoloCronoOpen] = useState(false);

  const toggleSel = (id) => setSel((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleExpand = (id) => setExpandidos((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const abrirFusion = (ids) => { setFusionIds(ids); setFusionAbierto(true); };
  const trasFusion = () => { setSel(new Set()); setFusionAbierto(false); setDupAbierto(false); cargar(); };

  useEffect(() => { api.get("/plantillas").then((r) => setPlantillas(r.data)); }, []);

  const cargar = useCallback(async () => {
    const { data } = await api.get("/servicios", { params: q ? { q } : {} });
    setServicios(data);
  }, [q]);

  useEffect(() => { const t = setTimeout(cargar, 300); return () => clearTimeout(t); }, [cargar]);

  const normales = servicios.filter((s) => !s.solo_cronograma);
  const soloCrono = servicios.filter((s) => s.solo_cronograma);

  const toggleSoloCronograma = async (s) => {
    const nuevo = !s.solo_cronograma;
    setServicios((prev) => prev.map((x) => (x.id === s.id ? { ...x, solo_cronograma: nuevo } : x)));
    try {
      await api.patch(`/servicios/${s.id}`, { solo_cronograma: nuevo });
      toast.success(nuevo ? "Marcado como solo cronograma" : "Disponible al ingresar trabajo");
    } catch (e) {
      setServicios((prev) => prev.map((x) => (x.id === s.id ? { ...x, solo_cronograma: !nuevo } : x)));
      toast.error(errorMsg(e));
    }
  };

  const plantillaNombre = (id) => plantillas.find((p) => p.id === id)?.nombre || id;

  const abrirEditar = (s) => {
    setForm({ id: s.id, nombre: s.nombre, activo: s.activo, solo_cronograma: !!s.solo_cronograma,
      solicita_repuestos: !!s.solicita_repuestos, abre_cronograma: !!s.abre_cronograma,
      garantia_honda: !!s.garantia_honda,
      plantillas_asociadas: [...(s.plantillas_asociadas || [])] });
    setErrores({});
  };

  const eliminar = async (s) => {
    if (!window.confirm(`¿Eliminar el servicio "${s.nombre}"?`)) return;
    try { await api.delete(`/servicios/${s.id}`); toast.success("Servicio eliminado"); cargar(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const guardar = async () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = "Ingrese nombre";
    setErrores(e);
    if (Object.keys(e).length) return;
    setGuardando(true);
    try {
      const body = { nombre: form.nombre, activo: form.activo, solo_cronograma: form.solo_cronograma,
        solicita_repuestos: form.solicita_repuestos, abre_cronograma: form.abre_cronograma,
        garantia_honda: form.garantia_honda,
        plantillas_asociadas: form.plantillas_asociadas };
      if (form.id) await api.patch(`/servicios/${form.id}`, body);
      else await api.post("/servicios", body);
      toast.success("Servicio guardado");
      setForm(null); setErrores({});
      cargar();
    } catch (err) {
      const msg = errorMsg(err);
      if (err?.response?.status === 409) setErrores({ nombre: msg });
      if (err?.response?.status === 422) setErrores((prev) => ({ ...prev, plantillas_asociadas: msg }));
      toast.error(msg);
    } finally { setGuardando(false); }
  };

  const set = (campo, v) => setForm({ ...form, [campo]: v });

  const renderTabla = (lista, emptyMsg, testId) => (
    <div className="overflow-x-auto">
      <table data-testid={testId} className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
            <th className="py-2.5 pr-3 font-semibold w-8"></th>
            <th className="py-2.5 pr-3 font-semibold">Nombre</th>
            <th className="py-2.5 pr-3 font-semibold">Plantillas asociadas</th>
            <th className="py-2.5 pr-3 font-semibold">Estado</th>
            <th className="py-2.5 pr-3 font-semibold">Solo cronograma</th>
            <th className="py-2.5 text-right font-semibold">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {lista.map((s) => {
            const pls = s.plantillas || [];
            const abierto = expandidos.has(s.id);
            return (
              <tr key={s.id} data-testid={`servicio-row-${s.id}`}
                className={`border-b border-slate-100 hover:bg-slate-50 align-top ${sel.has(s.id) ? "bg-indigo-50/40" : ""}`}>
                <td className="py-2.5 pr-3">
                  <input type="checkbox" data-testid={`servicio-sel-${s.id}`} checked={sel.has(s.id)}
                    onChange={() => toggleSel(s.id)} className="accent-indigo-600 w-4 h-4" />
                </td>
                <td className="py-2.5 pr-3 font-medium text-slate-800">{s.nombre}</td>
                <td className="py-2.5 pr-3 text-slate-600">
                  {pls.length === 0 ? (
                    <span className="text-slate-400">—</span>
                  ) : (
                    <>
                      <button type="button" data-testid={`servicio-plantillas-toggle-${s.id}`}
                        onClick={() => toggleExpand(s.id)}
                        className="inline-flex items-center gap-1 text-[11px] pl-2 pr-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium hover:bg-indigo-100">
                        <LayoutList size={12} /> {pls.length}
                        {!abierto && (
                          <span className="text-slate-500 font-normal max-w-[160px] truncate">
                            {pls[0].nombre}{pls.length > 1 ? ` +${pls.length - 1}` : ""}
                          </span>
                        )}
                        {abierto ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      </button>
                      {abierto && (
                        <ul data-testid={`servicio-plantillas-expandido-${s.id}`} className="mt-1.5 space-y-0.5">
                          {pls.map((p) => (
                            <li key={p.id} className="flex items-start gap-1.5 text-xs text-slate-600">
                              <span className="text-indigo-300 leading-5">•</span>{p.nombre}
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </td>
                <td className="py-2.5 pr-3"><EstadoBadge activo={s.activo} /></td>
                <td className="py-2.5 pr-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <Switch data-testid={`servicio-solo-cronograma-switch-${s.id}`}
                      checked={!!s.solo_cronograma} onCheckedChange={() => toggleSoloCronograma(s)} />
                    <span className={`text-xs font-medium ${s.solo_cronograma ? "text-amber-600" : "text-slate-400"}`}>
                      {s.solo_cronograma ? "Sí" : "No"}
                    </span>
                  </label>
                </td>
                <td className="py-2.5">
                  <div className="flex items-center justify-end gap-1.5">
                    <button data-testid={`servicio-editar-btn-${s.id}`} title="Editar" onClick={() => abrirEditar(s)}
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100">
                      <Pencil size={13} />
                    </button>
                    <button data-testid={`servicio-eliminar-btn-${s.id}`} title="Eliminar" onClick={() => eliminar(s)}
                      className="w-7 h-7 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                      <X size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
          {lista.length === 0 && (
            <tr><td colSpan={6} className="py-6 text-center text-slate-400" data-testid={`${testId}-vacio`}>
              {emptyMsg}
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div data-testid="servicios-page" className="space-y-4">
      <Panel titulo="Servicios" testId="servicios-panel">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-end mb-4">
          <Button variant="outline" data-testid="servicios-buscar-duplicados-btn"
            className="border-slate-300 text-slate-600 hover:bg-slate-50 sm:mr-auto" onClick={() => setDupAbierto(true)}>
            <Copy size={15} className="mr-1.5" /> Buscar posibles duplicados
          </Button>
          <Button data-testid="servicios-fusionar-btn" disabled={sel.size < 2}
            onClick={() => abrirFusion([...sel])}
            className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-40">
            <GitMerge size={15} className="mr-1.5" /> Fusionar seleccionados{sel.size >= 2 ? ` (${sel.size})` : ""}
          </Button>
          <Button data-testid="servicio-agregar-btn" onClick={() => { setForm(VACIO); setErrores({}); }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white">
            <Plus size={16} className="mr-1" /> Agregar
          </Button>
        </div>
        <div className="space-y-6">
          <section data-testid="servicios-seccion-normales" className="space-y-2">
            <h3 className="font-heading font-bold text-xs uppercase text-slate-600">
              Servicios <span className="text-slate-400">({normales.length})</span>
            </h3>
            {renderTabla(normales, "No hay servicios", "servicios-tabla")}
          </section>

          <section data-testid="servicios-seccion-solo-cronograma" className="space-y-2 border-t border-slate-200 pt-4">
            <button type="button" data-testid="servicios-solo-cronograma-toggle"
              onClick={() => setSoloCronoOpen((o) => !o)}
              className="flex items-center gap-1.5 font-heading font-bold text-xs uppercase text-amber-700 hover:text-amber-800">
              {soloCronoOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
              Solo cronograma <span className="text-amber-400">({soloCrono.length})</span>
            </button>
            {soloCronoOpen && renderTabla(soloCrono, "No hay servicios solo cronograma", "servicios-tabla-solo-cronograma")}
          </section>
        </div>
      </Panel>

      <ServicioFusionDialog abierto={fusionAbierto} ids={fusionIds}
        onCerrar={() => setFusionAbierto(false)} onFusionado={trasFusion} />
      <ServicioDuplicadosDialog abierto={dupAbierto} onCerrar={() => setDupAbierto(false)}
        onFusionarGrupo={(ids) => { setDupAbierto(false); abrirFusion(ids); }} />

      <Dialog open={!!form} onOpenChange={(o) => { if (!o) { setForm(null); setErrores({}); } }}>
        <DialogContent data-testid="servicio-dialog" className="max-w-2xl max-h-[90vh] flex flex-col gap-0 p-0">
          <DialogHeader className="shrink-0 px-6 pt-6 pb-3">
            <DialogTitle>{form?.id ? "Editar servicio" : "Nuevo servicio"}</DialogTitle>
            <DialogDescription>
              Las plantillas asociadas definen los repuestos y mano de obra estimados del servicio.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <>
              <div data-testid="servicio-dialog-body" className="flex-1 min-h-0 overflow-y-auto px-6 py-2 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Campo label="Nombre" obligatorio error={errores.nombre} testId="servicio-nombre">
                    <Input data-testid="servicio-nombre-input" value={form.nombre}
                      onChange={(e) => set("nombre", e.target.value)} placeholder="Ej: Mantenimiento preventivo" />
                  </Campo>
                  <Campo label="Estado" testId="servicio-activo">
                    <label className="flex items-center gap-3 h-9 text-sm text-slate-600">
                      <Switch data-testid="servicio-activo-switch" checked={form.activo}
                        onCheckedChange={(v) => set("activo", v)} />
                      {form.activo ? "Activo" : "Inactivo"}
                    </label>
                  </Campo>
                </div>
                <Campo label="Solo para cronogramas" testId="servicio-solo-cronograma">
                  <label className="flex items-center gap-3 h-9 text-sm text-slate-600">
                    <Switch data-testid="servicio-solo-cronograma-dialog-switch" checked={form.solo_cronograma}
                      onCheckedChange={(v) => set("solo_cronograma", v)} />
                    {form.solo_cronograma ? "Solo visible en cronogramas (no se ofrece al ingresar trabajo)" : "Disponible al ingresar trabajo"}
                  </label>
                </Campo>
                <Campo label="Solicita repuestos al agregarlo" testId="servicio-solicita-repuestos">
                  <label className="flex items-center gap-3 h-9 text-sm text-slate-600">
                    <Switch data-testid="servicio-solicita-repuestos-switch" checked={form.solicita_repuestos}
                      onCheckedChange={(v) => set("solicita_repuestos", v)} />
                    {form.solicita_repuestos ? "Al incluirlo en un trabajo, pide elegir los repuestos a cambiar" : "No pide repuestos"}
                  </label>
                </Campo>
                <Campo label="Abre cronograma de mantenimiento" testId="servicio-abre-cronograma">
                  <label className="flex items-center gap-3 h-9 text-sm text-slate-600">
                    <Switch data-testid="servicio-abre-cronograma-switch" checked={form.abre_cronograma}
                      onCheckedChange={(v) => set("abre_cronograma", v)} />
                    {form.abre_cronograma ? "Al agregarlo a un trabajo abre la ventana del cronograma del modelo" : "No abre cronograma"}
                  </label>
                </Campo>
                <Campo label="Reporte de garantía Honda" testId="servicio-garantia-honda">
                  <label className="flex items-center gap-3 h-9 text-sm text-slate-600">
                    <Switch data-testid="servicio-garantia-honda-switch" checked={form.garantia_honda}
                      onCheckedChange={(v) => set("garantia_honda", v)} />
                    {form.garantia_honda ? "Al agregarlo pide el reporte del cliente para la garantía Honda" : "No es un reporte de garantía"}
                  </label>
                </Campo>
                <Campo label="Plantillas asociadas" error={errores.plantillas_asociadas} testId="servicio-plantillas">
                  <MultiCombobox testId="servicio-plantillas-combobox" values={form.plantillas_asociadas}
                    onChange={(v) => set("plantillas_asociadas", v)} placeholder="Asociar plantillas del cotizador"
                    options={plantillas
                      .filter((p) => p.origen === "maestra" || p.origen === "manual")
                      .map((p) => ({ value: p.id, label: p.nombre }))} />
                  {form.plantillas_asociadas.length > 0 && (
                    <ul data-testid="servicio-plantillas-lista"
                      className="mt-2 space-y-1.5 max-h-[40vh] overflow-y-auto overscroll-contain pr-1">
                      {form.plantillas_asociadas.map((pid, i) => (
                        <li key={pid} data-testid={`servicio-plantilla-item-${pid}`}
                          className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm">
                          <span className="text-xs text-slate-400 w-4">{i + 1}.</span>
                          <span className="flex-1 truncate text-slate-700">{plantillaNombre(pid)}</span>
                          <button type="button" data-testid={`servicio-plantilla-quitar-${pid}`} title="Quitar"
                            onClick={() => set("plantillas_asociadas", form.plantillas_asociadas.filter((x) => x !== pid))}
                            className="w-5 h-5 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                            <X size={12} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Campo>
              </div>
              <div className="shrink-0 flex justify-end gap-2 border-t border-slate-100 bg-white px-6 py-4 rounded-b-lg">
                <Button variant="outline" data-testid="servicio-cancelar-btn"
                  className="border-teal-500 text-teal-600 hover:bg-teal-50 hover:text-teal-700"
                  onClick={() => { setForm(null); setErrores({}); }}>Cancelar</Button>
                <Button data-testid="servicio-guardar-btn" onClick={guardar} disabled={guardando}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6">
                  {guardando ? "Guardando..." : "Guardar"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
